import React, { useRef, useState } from "react";
import styled from "styled-components";
import { AlertTriangle, Search } from "react-feather";
import { useQuery, useQueryClient } from "react-query";
import Fuse from "fuse.js";
import {
  Banner,
  Input,
  PageWrapperTwoColumn,
  Select,
  SelectOption,
  Spinner,
} from "@cinesys/ui/components";
import SpotterHeader from "../SpotterHeader";
import SpotterChangesPanel, {
  SpotterChangeGroup,
  SpotterChangesPanelProps,
} from "../SpotterChangesPanel";
import SpotterEditedTag from "../SpotterEditedTag";
import SpotterNewTag from "../SpotterNewTag";
import CollapsibleFieldRow from "../CollapsibleFieldRow";
import AddOptionSelect from "../AddOptionSelect";
import { SPOTTER_STRINGS } from "../strings";
import { usePersistedState } from "../usePersistedState";
import { combineSpotterQueryErrors } from "../accessError";
import { useInterfaceAccess } from "../../../hooks/useInterfaceAccess";
import InterfaceAccessNotice from "../../../components/InterfaceAccessNotice";
import { DelayedSpinner } from "@cinesys/experience-ui/components";
import { SPOTTER_INTERFACE_ID } from "../config";
import { IconikMetadataView, getIconikViews } from "../mapping/service";
import {
  FrMappingPutEntry,
  FR_MAPPING_QUERY_KEY,
  useFrMapping,
  useFrPersons,
  useSaveFrMapping,
} from "../frMapping/service";
import { buildAddFieldOptions, FlatFieldEntry } from "../fields/addField";
import { dk, parseDk } from "../fields/fieldKey";
import { FR_BADGE_COLOR } from "../fields/modeColors";

/**
 * Spotter Facial Recognition tab — every row is an iconik destination field with the org's NAMED
 * FR persons currently routed to it. Its own tab/route (`spotter/facial-recognition`), independent
 * of the Fields tab's Mode picker — a field can be simultaneously Mapped (Fields) and FR-routed
 * (here) with no conflict, since the two stores never shared payload (see
 * `apps/customer-api/src/services/customerSpotterFrMapping.ts`'s doc comment).
 *
 * Built on the same shared row shell/add pattern Fields established (`../CollapsibleFieldRow.tsx`,
 * `../AddOptionSelect.tsx`) — no Mode picker here (every row is definitionally Facial Recognition,
 * so the row badge is a fixed constant, not driven by a per-row value). Field eligibility for
 * "Add Field" is any field on the org's configured views, full stop — no cross-tab exclusion
 * against Fields'/Glossary's claimed fields.
 *
 * Storage/API: `../frMapping/service.ts`'s `GET`/`PUT /api/spotter/fr-mapping`, backed by
 * `apps/customer-api/src/services/customerSpotterFrMapping.ts` — a person-keyed store, independent of the
 * unified Fields tab's `fieldsMap`. Eligibility is the org's live configured iconik views
 * (`GET /iconik-views`), not anything read from the Mapping store.
 *
 * PERSON ASSIGNMENT: a person can only ever be routed to ONE destination field at a time;
 * `assignments` is a `person_id -> destination key` map, so reassigning someone in one row's
 * multi-select structurally removes them from any other row's (see `renderFieldRow`'s multi-select
 * `onChange`) — the server re-validates the same invariant on save, since a map's own uniqueness
 * is a client-side convenience, not something the backend can trust.
 *
 * A field row with zero people routed persists nothing on Save (the store has no field-level "this
 * field is FR-enabled" marker of its own — only real person -> field assignments), so an
 * added-but-empty row simply won't reappear on the next load if left unpopulated.
 */

const Page = styled.div`
  min-height: 100%;
  color: var(--spotterInk);
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
  font-size: 15px;
  line-height: 1.5;
`;

/** Re-skins every `@cinesys/ui` `Select`/`Button` rendered inside this `Wrap` to the Spotter amber
 *  palette — see `apps/customer-frontend/CLAUDE.md`'s styling convention (this block is copied
 *  verbatim from `../fields/FieldsScreen.tsx`'s equivalent `Wrap`). */
const Wrap = styled.div`
  padding: 0 22px;
  max-width: 1520px;
  margin: 0 auto;

  --inputBackground: var(--spotterRaised);
  --inputBorder: var(--spotterLine2);
  --dark400: var(--spotterRaised);
  --dark500: var(--spotterLine2);
  --dark600: var(--spotterLine2);
  --dark700: var(--spotterInkFaint);
  --gray400: var(--spotterInk);
  --gray500: var(--spotterInk);
  --blue060: var(--spotterAccent);
  --blue100: var(--spotterAccent);
  --selectOptionHover: var(--spotterAccentHover);

  --blue400: var(--spotterAccent);
  --blue500: var(--spotterAccent);
  --blueBorder: var(--spotterAccentLine);
  --blueBorderOverlay: var(--spotterAccentLine);
  --blueBackground: var(--spotterAccentSoft);
  --blueFocusRing: var(--spotterAccentSoft);

  --green400: var(--spotterOk);
  --green500: var(--spotterOk);
  --greenBorder: var(--spotterOkLine);
  --greenBorderOverlay: var(--spotterOkLine);
  --greenBackground: var(--spotterOkSoft);

  --red400: var(--spotterDanger);
  --red500: var(--spotterDanger);
  --redBorder: var(--spotterDangerBorder);
  --redBorderOverlay: var(--spotterDangerBorder);
  --redBackground: var(--spotterDangerSoft);
`;

const Main = styled.main`
  padding-top: 20px;
  padding-bottom: 100px;
`;

const SelectPortalHost = styled.div`
  display: contents;
`;

const Note = styled.div`
  display: flex;
  gap: 12px;
  padding: 13px 15px;
  border-radius: 10px;
  background: var(--spotterAccentSoft);
  border: 1px solid var(--spotterAccentLine);
  margin-bottom: 6px;
  font-size: 14px;
  color: var(--spotterInk);
  line-height: 1.5;
`;

const NoteRule = styled.div`
  width: 3px;
  border-radius: 3px;
  background: var(--spotterAccent);
  flex: 0 0 auto;
`;

const SearchAddRow = styled.div`
  position: sticky;
  top: var(--spotterHeaderHeight, 98px);
  z-index: 5;
  background: var(--spotterPaper);
  margin: 0 -10px;
  padding: 10px 10px;

  @media (max-width: 700px) {
    position: static;
  }
`;

const SearchAddRowBody = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 700px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchWrap = styled.div`
  flex: 1 1 0;
  min-width: 0;
`;

const NoFieldsMessage = styled.p`
  padding: 40px 15px;
  margin: 0;
  text-align: center;
  font-size: 14px;
  color: var(--spotterInkSoft);
`;

const FieldRows = styled.div<{ $disabled: boolean }>`
  margin-top: 2px;

  ${(p) =>
    p.$disabled &&
    `
    pointer-events: none;
    opacity: 0.6;
  `}
`;

/** Fixed row badge — every row on this tab is Facial Recognition, so unlike Fields' `TypeTag` this
 *  isn't driven by a per-row value. Reuses `FR_BADGE_COLOR` (the color Facial Recognition had as a
 *  Fields-tab mode) for visual continuity. */
const Badge = styled.span`
  flex: 0 0 auto;
  margin-left: 5px;
  color: ${FR_BADGE_COLOR.fg};
  background: ${FR_BADGE_COLOR.bg};
  border: 1px solid ${FR_BADGE_COLOR.line};
  border-radius: 6px;
  padding: 2px 7px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-family: ui-monospace, Menlo, monospace;
  white-space: nowrap;
`;

const PanelHint = styled.p`
  font-size: 14px;
  color: var(--spotterInkSoft);
  margin: 0 0 11px;
  line-height: 1.5;
`;

/** Same drop-down-destination caveat Fields shows on a non-Glossary row (`fields.dropdownWarning`)
 *  — a drop-down field only accepts values already configured as options on it, and an FR-routed
 *  person's name is very unlikely to be one of those. */
const WarningHint = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 7px;
  font-size: 13px;
  color: var(--spotterAccent);
  background: var(--spotterAccentSoft);
  border: 1px solid var(--spotterAccentLine);
  border-radius: 6px;
  padding: 8px 10px;
  margin: 0 0 11px;
  line-height: 1.45;

  svg {
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

const Aside = styled.aside`
  padding-top: 20px;
  padding-bottom: 20px;

  @media (min-width: 1030px) {
    position: sticky;
    top: var(--spotterHeaderHeight, 98px);
    max-height: calc(100vh - var(--spotterHeaderHeight, 98px));
    overflow-y: auto;
    margin: 0 -14px;
    padding: 20px 14px 20px;
  }
`;

const AsideChangesPanel = styled.div`
  display: none;

  @media (min-width: 1030px) {
    display: block;
  }
`;

/** Change-panel pseudo-destination for a person removed entirely (no `assignments` entry left) —
 *  never collides with a real `dk()` key since those always contain `::`. Groups that person's
 *  change under an "Unassigned" heading; never sent to the backend. */
const FR_UNASSIGNED_GROUP_KEY = "__unassigned__";

const FrScreen = () => {
  const access = useInterfaceAccess(SPOTTER_INTERFACE_ID);
  const queryClient = useQueryClient();

  const viewsQuery = useQuery<IconikMetadataView[]>(
    "spotter-views",
    getIconikViews
  );
  const frPersonsQuery = useFrPersons();
  const frMappingQuery = useFrMapping();
  const saveMutation = useSaveFrMapping();

  // `person_id -> destination key` — a map can hold at most one destination per person, so mutual
  // exclusivity is structural, not a rule this state has to enforce separately.
  const [assignments, setAssignments] = usePersistedState<
    Record<string, string>
  >("spotter-fr-assignments", {});
  const [originalAssignments, setOriginalAssignments] = usePersistedState<
    Record<string, string>
  >("spotter-fr-assignments-original", {});
  const [order, setOrder] = usePersistedState<string[]>("spotter-fr-order", []);
  const [originalOrder, setOriginalOrder] = usePersistedState<string[]>(
    "spotter-fr-order-original",
    []
  );
  const [initialized, setInitialized] = usePersistedState(
    "spotter-fr-initialized",
    false
  );
  const [selectPortal, setSelectPortal] = useState<HTMLDivElement | null>(null);
  const [search, setSearch] = useState("");
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [removingKeys, setRemovingKeys] = useState<Set<string>>(new Set());
  const [justAddedKey, setJustAddedKey] = useState<string | null>(null);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});

  React.useEffect(() => {
    if (!justAddedKey) {
      return;
    }
    rowRefs.current[justAddedKey]?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
    setJustAddedKey(null);
  }, [justAddedKey]);

  const toggleExpanded = (key: string): void => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  React.useEffect(() => {
    if (!initialized && viewsQuery.data && frMappingQuery.data) {
      const initialAssignments: Record<string, string> = {};
      for (const entry of frMappingQuery.data.entries) {
        initialAssignments[entry.person_id] = dk(
          entry.to_view_id ?? "",
          entry.scope ?? "asset",
          entry.destination_field
        );
      }
      setAssignments(initialAssignments);
      setOriginalAssignments(initialAssignments);

      const labelFor = (key: string): string => {
        const parsed = parseDk(key);
        if (!parsed) {
          return key;
        }
        const view = viewsQuery.data?.find((v) => v.id === parsed.viewId);
        const field = view?.view_fields.find(
          (f) => f.name === parsed.fieldName
        );
        return field?.label || parsed.fieldName;
      };
      const initialOrder = Array.from(
        new Set(Object.values(initialAssignments))
      ).sort((a, b) => labelFor(a).localeCompare(labelFor(b)));
      setOrder(initialOrder);
      setOriginalOrder(initialOrder);
      setInitialized(true);
    }
  }, [initialized, viewsQuery.data, frMappingQuery.data]);

  // Org-level entitlement gate — see FieldsScreen.tsx's equivalent doc comment.
  if (access.status !== "granted") {
    return (
      <Page>
        <SpotterHeader />
        {access.status === "denied" && (
          <Wrap>
            <InterfaceAccessNotice reason={access.reason} />
          </Wrap>
        )}
        {access.status === "pending" && <DelayedSpinner padTop />}
      </Page>
    );
  }

  const isLoading =
    viewsQuery.isLoading ||
    frPersonsQuery.isLoading ||
    frMappingQuery.isLoading;

  if (isLoading) {
    return (
      <Page>
        <SpotterHeader />
        <Spinner padTop />
      </Page>
    );
  }

  const { hasError, accessDenied } = combineSpotterQueryErrors([
    viewsQuery,
    frPersonsQuery,
    frMappingQuery,
  ]);

  if (hasError) {
    return (
      <Page>
        <SpotterHeader />
        <Wrap>
          <Banner
            error={
              accessDenied
                ? SPOTTER_STRINGS.facialRecognition.accessDeniedError
                : SPOTTER_STRINGS.facialRecognition.loadError
            }
            padTop
          />
        </Wrap>
      </Page>
    );
  }

  const views = viewsQuery.data ?? [];
  const assetView = views.find((v) => v.scope === "asset");
  const segmentView = views.find((v) => v.scope === "segment");
  const fieldEntriesFor = (
    view: IconikMetadataView | undefined
  ): FlatFieldEntry[] =>
    view
      ? (view.view_fields ?? []).map((field) => ({
          view,
          field,
          key: dk(view.id, view.scope, field.name),
        }))
      : [];
  const flatFieldEntries: FlatFieldEntry[] = [
    ...fieldEntriesFor(assetView),
    ...fieldEntriesFor(segmentView),
  ];
  const flatFieldEntryByKey = new Map(
    flatFieldEntries.map((e): [string, FlatFieldEntry] => [e.key, e])
  );

  const fieldLabelFor = (key: string): string => {
    const parsed = parseDk(key);
    if (!parsed) {
      return key;
    }
    return flatFieldEntryByKey.get(key)?.field.label ?? parsed.fieldName;
  };

  const personLabelById = new Map(
    (frPersonsQuery.data ?? []).map((p) => [p.id, p.name])
  );

  // Which destination field NAMES already have a row on THIS tab — no cross-tab exclusion against
  // Fields'/Glossary's claimed fields (see the file doc comment).
  const addedFieldNames = new Set(
    order.map((key) => parseDk(key)?.fieldName).filter((n): n is string => !!n)
  );
  const addFieldOptions = buildAddFieldOptions(
    flatFieldEntries,
    addedFieldNames,
    {
      asset: SPOTTER_STRINGS.fields.assetMetadataLabel,
      segment: SPOTTER_STRINGS.fields.timedMetadataLabel,
    },
    new Set(),
    () => undefined
  );

  const clearAssignmentsFor = (destinationKey: string): void => {
    setAssignments((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const [personId, dest] of Object.entries(prev)) {
        if (dest === destinationKey) {
          delete next[personId];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  };

  const handleAddField = (key: string): void => {
    setOrder((prev) => [key, ...prev]);
    setExpandedKeys((prev) => new Set(prev).add(key));
    setJustAddedKey(key);
  };

  const handleRemoveField = (key: string): void => {
    clearAssignmentsFor(key);
    setOrder((prev) => prev.filter((k) => k !== key));
    setExpandedKeys((prev) => {
      if (!prev.has(key)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const handleSave = (): void => {
    const outEntries: FrMappingPutEntry[] = Object.entries(assignments)
      .map(([personId, destinationKey]): FrMappingPutEntry | undefined => {
        const parsed = parseDk(destinationKey);
        if (!parsed) {
          return undefined;
        }
        return {
          person_id: personId,
          person_label: personLabelById.get(personId) ?? "",
          to_view_id: parsed.viewId,
          to_field_id: parsed.fieldName,
        };
      })
      .filter((e): e is FrMappingPutEntry => !!e);

    saveMutation.mutate(outEntries, {
      onSuccess: (result) => {
        queryClient.setQueryData(FR_MAPPING_QUERY_KEY, result);
        const savedAssignments: Record<string, string> = {};
        for (const entry of result.entries) {
          savedAssignments[entry.person_id] = dk(
            entry.to_view_id ?? "",
            entry.scope ?? "asset",
            entry.destination_field
          );
        }
        setAssignments(savedAssignments);
        setOriginalAssignments(savedAssignments);
        // Never auto-prune a row the user didn't explicitly remove — a row that saved down to
        // zero people (or was just added and never populated) simply stays visible, still
        // editable, until Remove is clicked.
        const savedKeys = Array.from(new Set(Object.values(savedAssignments)));
        setOrder((prev) => Array.from(new Set([...prev, ...savedKeys])));
      },
    });
  };

  const handleCancel = (): void => {
    setAssignments(originalAssignments);
    setOrder(originalOrder);
    saveMutation.reset();
  };

  // One item per person whose assignment changed, grouped by their CURRENT destination
  // (`FR_UNASSIGNED_GROUP_KEY` if removed entirely) — a person moved between fields shows up
  // once, under wherever they ended up, not where they left.
  const changedPersonIds = Array.from(
    new Set([...Object.keys(assignments), ...Object.keys(originalAssignments)])
  ).filter((id) => assignments[id] !== originalAssignments[id]);
  const changedIdsByDestination = new Map<string, string[]>();
  for (const personId of changedPersonIds) {
    const destKey = assignments[personId] ?? FR_UNASSIGNED_GROUP_KEY;
    const existing = changedIdsByDestination.get(destKey);
    if (existing) {
      existing.push(personId);
    } else {
      changedIdsByDestination.set(destKey, [personId]);
    }
  }
  const personChangeItem = (
    personId: string,
    destKey: string
  ): {
    id: string;
    label: React.ReactNode;
    status?: "added" | "removed" | "edited";
  } => {
    const name = personLabelById.get(personId) ?? personId;
    const originalDest = originalAssignments[personId];
    if (!originalDest) {
      return { id: personId, label: name, status: "added" };
    }
    if (destKey === FR_UNASSIGNED_GROUP_KEY) {
      return { id: personId, label: name, status: "removed" };
    }
    return {
      id: personId,
      label: SPOTTER_STRINGS.facialRecognition.changes.personMoved(
        name,
        fieldLabelFor(originalDest)
      ),
      status: "edited",
    };
  };
  const changeGroupsByKey = new Map<string, SpotterChangeGroup>();
  for (const [destKey, personIds] of Array.from(
    changedIdsByDestination.entries()
  )) {
    changeGroupsByKey.set(destKey, {
      id: destKey,
      label:
        destKey === FR_UNASSIGNED_GROUP_KEY
          ? SPOTTER_STRINGS.facialRecognition.changes.unassignedLabel
          : fieldLabelFor(destKey),
      isNew:
        destKey !== FR_UNASSIGNED_GROUP_KEY && !originalOrder.includes(destKey),
      items: personIds.map((personId) => personChangeItem(personId, destKey)),
    });
  }
  const changeGroups = Array.from(changeGroupsByKey.values());
  const changedKeySet = new Set(
    Array.from(changeGroupsByKey.keys()).filter(
      (k) => k !== FR_UNASSIGNED_GROUP_KEY
    )
  );

  const isFieldNew = (key: string): boolean => !originalOrder.includes(key);

  const search_ = search.trim();
  const searchableRows = order.map((key) => {
    const assignedIds = Object.entries(assignments)
      .filter(([, dest]) => dest === key)
      .map(([personId]) => personId);
    return {
      key,
      label: fieldLabelFor(key),
      personLabels: assignedIds.map((id) => personLabelById.get(id) ?? id),
    };
  });
  const visibleKeys = search_
    ? new Set(
        new Fuse(searchableRows, {
          keys: ["label", "personLabels"],
          threshold: 0.4,
          shouldSort: false,
        })
          .search(search_)
          .map((r) => r.item.key)
      )
    : new Set(order);

  const changesProps: SpotterChangesPanelProps = {
    groups: changeGroups,
    onSave: handleSave,
    onCancel: handleCancel,
    isSaving: saveMutation.isLoading,
    saveError: saveMutation.isError
      ? SPOTTER_STRINGS.facialRecognition.saveError
      : null,
    saveSuccess: saveMutation.isSuccess,
  };

  const renderFieldRow = (key: string): JSX.Element | null => {
    const parsed = parseDk(key);
    if (!parsed) {
      return null;
    }
    const displayLabel = fieldLabelFor(key);
    const isNew = isFieldNew(key);
    const isEdited = changedKeySet.has(key) && !isNew;
    const expanded = expandedKeys.has(key);
    const isRemoving = removingKeys.has(key);
    const assignedIds = Object.entries(assignments)
      .filter(([, dest]) => dest === key)
      .map(([personId]) => personId);
    const personOptions = (frPersonsQuery.data ?? []).map((p) => ({
      value: p.id,
      label: p.name,
    }));

    return (
      <CollapsibleFieldRow
        key={key}
        ref={(el) => {
          rowRefs.current[key] = el;
        }}
        isNew={isNew}
        isEdited={isEdited}
        isRemoving={isRemoving}
        onRemoveTransitionEnd={() => {
          handleRemoveField(key);
          setRemovingKeys((prev) => {
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
        }}
        expanded={expanded}
        onToggleExpand={() => toggleExpanded(key)}
        expandTitle={SPOTTER_STRINGS.facialRecognition.expandRowTitle}
        collapseTitle={SPOTTER_STRINGS.facialRecognition.collapseRowTitle}
        label={displayLabel}
        badge={<Badge>{SPOTTER_STRINGS.facialRecognition.badgeLabel}</Badge>}
        summary={
          assignedIds.length > 0
            ? SPOTTER_STRINGS.facialRecognition.peopleCount(assignedIds.length)
            : undefined
        }
        headerRightExtra={
          changedKeySet.has(key) &&
          (isNew ? <SpotterNewTag /> : <SpotterEditedTag />)
        }
        removeLabel={SPOTTER_STRINGS.facialRecognition.removeFieldButton}
        onRemoveClick={() => setRemovingKeys((prev) => new Set(prev).add(key))}
      >
        {flatFieldEntryByKey.get(key)?.field.field_type === "drop_down" && (
          <WarningHint>
            <AlertTriangle size={14} />
            {SPOTTER_STRINGS.fields.dropdownWarning}
          </WarningHint>
        )}
        <PanelHint>{SPOTTER_STRINGS.facialRecognition.hint}</PanelHint>
        <Select
          label={SPOTTER_STRINGS.facialRecognition.personsLabel}
          isMulti
          isSearchable
          options={personOptions}
          placeholder={SPOTTER_STRINGS.facialRecognition.personsPlaceholder}
          value={assignedIds}
          onChange={(picked) => {
            const pickedIds = new Set(
              (picked as SelectOption[]).map((o) => o.value as string)
            );
            setAssignments((prev) => {
              const next = { ...prev };
              for (const [personId, dest] of Object.entries(prev)) {
                if (dest === key && !pickedIds.has(personId)) {
                  delete next[personId];
                }
              }
              pickedIds.forEach((personId) => {
                next[personId] = key;
              });
              return next;
            });
          }}
          onClear={() => clearAssignmentsFor(key)}
          portalTarget={selectPortal}
          noPadding
        />
      </CollapsibleFieldRow>
    );
  };

  return (
    <Page>
      <SpotterHeader changes={changesProps} />
      <Wrap>
        <SelectPortalHost ref={setSelectPortal} />
        <PageWrapperTwoColumn
          minWidth="1030px"
          id="spotter-facial-recognition"
          padding="0"
          defaultRatio={0.72}
          stickyTop="var(--spotterHeaderHeight, 98px)"
        >
          <Main>
            <Note>
              <NoteRule />
              <div>{SPOTTER_STRINGS.facialRecognition.intro}</div>
            </Note>

            <SearchAddRow>
              <SearchAddRowBody>
                <SearchWrap>
                  <Input
                    noPadding
                    icon={Search}
                    placeholder={
                      SPOTTER_STRINGS.facialRecognition.searchPlaceholder
                    }
                    value={search}
                    onChange={setSearch}
                  />
                </SearchWrap>
                <AddOptionSelect
                  placeholder={SPOTTER_STRINGS.facialRecognition.addFieldButton}
                  options={addFieldOptions}
                  onSelect={handleAddField}
                  portalTarget={selectPortal}
                />
              </SearchAddRowBody>
            </SearchAddRow>

            <FieldRows $disabled={saveMutation.isLoading}>
              {order.length === 0 ? (
                <NoFieldsMessage>
                  {SPOTTER_STRINGS.facialRecognition.noFieldsMessage}
                </NoFieldsMessage>
              ) : (
                order
                  .filter((key) => visibleKeys.has(key))
                  .map((key) => renderFieldRow(key))
              )}
            </FieldRows>
          </Main>
          <Aside>
            <AsideChangesPanel>
              <SpotterChangesPanel {...changesProps} />
            </AsideChangesPanel>
          </Aside>
        </PageWrapperTwoColumn>
      </Wrap>
    </Page>
  );
};

export default FrScreen;
